// Auth endpoints — shapes mirror api/src/http/routes/auth.ts exactly.
import { z } from "zod";
import { apiFetch } from "./client";

export type TierId = "free" | "small" | "medium" | "large" | "xlarge";

export interface RegisterRequest {
  orgName: string;
  email: string;
  password: string;
  adminName?: string;
  tier?: TierId;
  acceptedTerms: true;
  termsVersion?: string;
}

export interface RegisterResponse {
  /** Plaintext Org_ID — shown exactly once, never retrievable again. */
  orgId: string;
  org: {
    id: string;
    name: string;
    orgScope: string;
    tier: TierId;
    status: string;
    createdAt: string;
    updatedAt: string;
  };
  admin: {
    id: string;
    orgId: string;
    email: string;
    name?: string;
    role: "owner";
    createdAt: string;
  };
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  expiresInSeconds: number;
  // role lets the portal gate nav/actions; the server stays authoritative.
  admin: { id: string; orgId: string; email: string; role: "owner" | "admin" | "member" };
}

export interface VerifyResponse {
  verified: true;
  email: string;
}

export interface AcceptInviteResponse {
  accepted: true;
  email: string;
}

export function register(body: RegisterRequest): Promise<RegisterResponse> {
  return apiFetch<RegisterResponse>("/auth/register", { method: "POST", body });
}

// The identity-bearing response is runtime-validated (vault Type_Assertion: prefer
// validation over an unchecked cast for API responses) — a drifted/missing token
// fails loud at the boundary as a ContractError, not a downstream undefined.
export const loginResponseSchema = z.object({
  token: z.string().min(1),
  expiresInSeconds: z.number(),
  admin: z.object({
    id: z.string(),
    orgId: z.string(),
    email: z.string(),
    role: z.enum(["owner", "admin", "member"]),
  }),
}) satisfies z.ZodType<LoginResponse>;

export function login(body: LoginRequest): Promise<LoginResponse> {
  return apiFetch("/auth/login", { method: "POST", body, schema: loginResponseSchema });
}

// Social sign-up (Phase 7): no password — the `social` handoff token (from the OAuth
// callback) carries the provider-verified identity. T&C acceptance still required. The
// API creates the org + links the provider + returns a session (same shape as login).
export interface RegisterSocialRequest {
  orgName: string;
  adminName?: string;
  tier?: TierId;
  acceptedTerms: true;
  termsVersion?: string;
  social: string;
}

// Social sign-up returns the login session PLUS the one-time Org_ID (the social path has
// no password step, so this response is the only chance to reveal it). Validated at the
// boundary — a missing orgId fails loud as a ContractError, never a silent undefined that
// would strand the user with an unshowable Org_ID (the exact bug this fixes).
export const socialRegisterResponseSchema = loginResponseSchema.extend({
  orgId: z.string().min(1),
});
export type SocialRegisterResponse = z.infer<typeof socialRegisterResponseSchema>;

export function registerSocial(body: RegisterSocialRequest): Promise<SocialRegisterResponse> {
  return apiFetch("/auth/register", { method: "POST", body, schema: socialRegisterResponseSchema });
}

// Current admin identity (incl. role) for the active bearer — used to establish a
// session after a social-login redirect (the JWT omits role; server-authoritative).
// hasPassword (COG-12) tells the Settings screen whether to show Change- or Set-password.
export const meSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  email: z.string(),
  role: z.enum(["owner", "admin", "member"]),
  hasPassword: z.boolean(),
});
export type MeResponse = z.infer<typeof meSchema>;

export function getMe(signal?: AbortSignal): Promise<MeResponse> {
  return apiFetch("/auth/me", { auth: true, schema: meSchema, signal });
}

// --- Account password management (COG-12) — the signed-in admin acts on THEIR OWN
//     account; the server takes the actor from the JWT (no id in the body). ---

// Change an existing password (email/password account). 204 → void on success; a wrong
// current password / too-short new one surfaces as an ApiError(400).
export function changePassword(body: { currentPassword: string; newPassword: string }): Promise<void> {
  return apiFetch<void>("/admin/account/password", { method: "POST", body, auth: true });
}

// Set a FIRST password (social account with none). Returns { hasPassword: true }; a
// password-already-set / too-short value surfaces as an ApiError(400).
export function setPassword(body: { newPassword: string }): Promise<void> {
  return apiFetch<void>("/admin/account/set-password", { method: "POST", body, auth: true });
}

export function verifyEmail(token: string): Promise<VerifyResponse> {
  return apiFetch<VerifyResponse>(`/auth/verify?token=${encodeURIComponent(token)}`);
}

// An invited member sets their password to activate the account (consumes the
// one-time invite token). Mirrors api acceptInviteSchema: token + password(min8).
export function acceptInvite(token: string, password: string): Promise<AcceptInviteResponse> {
  return apiFetch<AcceptInviteResponse>("/auth/accept-invite", {
    method: "POST",
    body: { token, password },
  });
}

export interface ResetPasswordResponse {
  reset: true;
  email: string;
}

// Account recovery (Phase 1). forgot-password + resend-verification ALWAYS resolve
// to a generic { ok: true } — the API never reveals whether the email exists
// (enumeration-safe), so the UI shows the same "if it exists, we've sent it" message
// either way.
export function forgotPassword(email: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>("/auth/forgot-password", { method: "POST", body: { email } });
}

// Consume a one-time reset token and set a new password. Mirrors api
// resetPasswordSchema: token + password(min8).
export function resetPassword(token: string, password: string): Promise<ResetPasswordResponse> {
  return apiFetch<ResetPasswordResponse>("/auth/reset-password", { method: "POST", body: { token, password } });
}

export function resendVerification(email: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>("/auth/resend-verification", { method: "POST", body: { email } });
}
